import React, { useEffect, useMemo, useState } from 'react';
import type { InvoiceFormFragment, InvoicesByIdQuery } from '../../graphql';
import {
  CostType,
  Currency,
  useCreateInvoiceItemMutation,
  useCreateInvoiceItemOrderMutation,
  useCreateInvoiceMutation,
  useDeleteInvoiceItemMutation,
  useDeleteInvoiceItemOrderByIdMutation,
  useDeleteInvoiceMutation,
  useInvoicesByIdQuery,
  useUpdateInvoiceItemMutation,
  useUpdateInvoiceMutation,
} from '../../graphql';
import {
  Box,
  Button,
  DatePicker,
  Form,
  FormField,
  FormRow,
  FormSection,
  Heading,
  Input,
  Select,
  Text,
} from '../ui';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import * as zod from 'zod';
import { stringToNumber } from '../../lib/zod-util';
import ReactSelect from 'react-select';
import { zodResolver } from '@hookform/resolvers/zod';
import { assertDefined, delay, isDefined } from '../../lib/util';
import { stringToNumOpt } from '../core/util';
import { gql } from '@apollo/client';
import { formatNumber } from '../../lib/format';
import { DefaultMap } from '@seaber/common/lib/collections';

export interface InvoicesProps {
  voyageCharterId: number;
}

type Invoice = InvoiceFormFragment;

const InvoiceSchema = zod.object({
  id: zod.number().nullable(),
  payingOrganizationId: zod.number().nullable().optional(),
  invoiceNumber: zod.string().nullable().optional(),
  currency: zod.nativeEnum(Currency),
  dueDate: zod.date(),
  invoiceItems: zod
    .array(
      zod.object({
        id: zod.number().nullable(),
        costType: zod.nativeEnum(CostType).nullable().optional(),
        amount: zod.number().nullable().optional(),
        note: zod.string().nullable().optional(),
        portCallId: zod.number().nullable().optional(),
        relatedOrders: zod.array(
          zod.object({
            id: zod.number().nullable(),
            label: zod.string(),
          }),
        ),
      }),
    )
    .optional(),
});

type InvoiceFormFields = zod.infer<typeof InvoiceSchema>;

type Options = { label: string; id: number }[];
type NullableOptions = { label: string; id: number | null }[];

function getInvoiceFormOptions(data: InvoicesByIdQuery | undefined) {
  const orders = data?.voyageCharterById;
  if (!orders) {
    return {
      orderOptions: [],
      portCallOptions: [],
      payingOrganizationOptions: [],
    };
  }

  const orderOptions: Options = [];
  const portCallMap = new Map<number, string>();
  const portCallLoadOrders = new DefaultMap<number, string[]>(() => []);
  const portCallDischargeOrders = new DefaultMap<number, string[]>(() => []);
  for (const o of orders.orderVoyageCharterRelationsByVoyageCharterId.nodes) {
    const order = o?.orderByOrderId;
    if (!order) continue;
    const { id, orderDetailsByOrderId, assortmentByAssortmentId } = order;
    const label = [
      orderDetailsByOrderId.nodes[0]?.orderName || '#' + id,
      assortmentByAssortmentId?.name,
    ]
      .filter(Boolean)
      .join(' ');
    orderOptions.push({
      id,
      label,
    });

    const relations = order?.orderPortCallRelationsByOrderId?.nodes;
    if (!relations) continue;
    for (const rel of relations) {
      if (!rel) continue;
      const isLoadPort = rel.isLoadPort;
      const portCall = rel.portCallByPortCallId;
      if (!portCall) continue;
      const portCallId = portCall.id;
      const name =
        portCall.locationByLocationId?.name || String(portCall.locationId);
      portCallMap.set(portCallId, name);
      (isLoadPort ? portCallLoadOrders : portCallDischargeOrders)
        .get(portCallId)
        .push(label);
    }
  }
  const portCalls = Array.from(portCallMap.entries());

  const portCallOptions: NullableOptions = [
    { label: 'n/a', id: null },
    ...portCalls.map(([id, name]) => {
      const load = portCallLoadOrders.get(id).join(', ');
      const discharge = portCallDischargeOrders.get(id).join(', ');
      return {
        label: `${name}${load ? ` +(${load})` : ''}${
          discharge ? ` -(${discharge})` : ''
        }`,
        id,
      };
    }),
  ];

  const payingOrganizationOptions = [
    { id: null, label: 'n/a' },
    ...(data?.allOrganizations?.nodes.filter(isDefined).map((org) => ({
      id: org.id ?? -1,
      label: org.name ?? 'UNNAMED ORGANIZATION',
    })) ?? []),
  ];

  return {
    orderOptions,
    portCallOptions,
    payingOrganizationOptions,
  };
}

function getInvoiceDefaultValues(orderOptions: Options, invoice?: Invoice) {
  const dueDate = invoice?.dueDate;
  const invoiceItems: InvoiceFormFields['invoiceItems'] = [];
  const defaultValues: InvoiceFormFields = {
    id: invoice?.id ?? null,
    invoiceNumber: invoice?.invoiceNumber ?? null,
    currency: invoice?.currency ?? Currency.EUR,
    dueDate: typeof dueDate === 'string' ? new Date(dueDate) : new Date(),
    invoiceItems: invoiceItems,
    payingOrganizationId: invoice?.payingOrganizationId,
  };

  if (invoice) {
    for (const item of invoice.invoiceItemsByInvoiceId.nodes) {
      if (!item) continue;
      const {
        id,
        invoiceItemOrdersByInvoiceItemId,
        note,
        costType,
        amount,
        portCallId,
      } = item;
      const relatedOrders: Options = [];
      for (const o of invoiceItemOrdersByInvoiceItemId.nodes) {
        if (!o) continue;
        const option = orderOptions.find((op) => op.id === o.orderId);
        if (option) relatedOrders.push(option);
      }
      invoiceItems.push({
        id,
        note,
        costType,
        amount: stringToNumOpt(amount),
        portCallId,
        relatedOrders,
      });
    }
  }

  return defaultValues;
}

const costTypeOptions = Object.keys(CostType).map((c) => ({
  name: c[0] + c.slice(1).toLowerCase(),
  value: c,
}));

const currencyOptions = Object.keys(Currency).map((c) => ({
  name: c,
  value: c,
}));

const InvoiceForm = ({
  invoice,
  onSave,
  onClose,
  formOptions,
  voyageCharterId,
}: {
  invoice?: Invoice;
  onSave: () => void;
  onClose?: () => void;
  formOptions: {
    orderOptions: Options;
    portCallOptions: NullableOptions;
    payingOrganizationOptions: NullableOptions;
  };
  voyageCharterId: number;
}): JSX.Element | null => {
  const { orderOptions, portCallOptions, payingOrganizationOptions } =
    formOptions;
  const [
    updateInvoice,
    { error: updateInvoiceError, loading: updateInvoiceLoading },
  ] = useUpdateInvoiceMutation();
  const [
    createInvoice,
    { error: createInvoiceError, loading: createInvoiceLoading },
  ] = useCreateInvoiceMutation({
    update: (cache, { data }) => {
      const newInvoice = data?.createInvoice?.invoice;
      if (!newInvoice) return;

      const newInvoiceRef = cache.writeFragment({
        data: newInvoice,
        fragment: gql`
          fragment NewInvoice on Invoice {
            id
            currency
            dueDate
            invoiceNumber
            voyageCharterId
            payingOrganizationId
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'VoyageCharter',
          id: newInvoice.voyageCharterId,
        }),
        fields: {
          invoicesByVoyageCharterId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newInvoiceRef],
          }),
        },
      });
    },
  });

  const [
    deleteInvoice,
    { error: deleteInvoiceError, loading: deleteInvoiceLoading },
  ] = useDeleteInvoiceMutation({
    update: (cache, { data }) => {
      const deletedInvoice = data?.deleteInvoiceById?.invoice;
      if (!deletedInvoice) return;
      cache.evict({ id: `Invoice:${deletedInvoice.id}` });
      cache.gc();
    },
  });

  const [
    updateInvoiceItem,
    { error: updateInvoiceItemError, loading: updateInvoiceItemLoading },
  ] = useUpdateInvoiceItemMutation();
  const [
    createInvoiceItem,
    { error: createInvoiceItemError, loading: createInvoiceItemLoading },
  ] = useCreateInvoiceItemMutation({
    update: (cache, { data }) => {
      const newInvoiceItem = data?.createInvoiceItem?.invoiceItem;
      if (!newInvoiceItem) return;

      const newInvoiceItemRef = cache.writeFragment({
        data: newInvoiceItem,
        fragment: gql`
          fragment NewInvoiceItem on InvoiceItem {
            id
            amount
            costType
            note
            portCallId
            invoiceId
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'Invoice',
          id: newInvoiceItem.invoiceId,
        }),
        fields: {
          invoiceItemsByInvoiceId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newInvoiceItemRef],
          }),
        },
      });
    },
  });
  const [
    deleteInvoiceItem,
    { error: deleteInvoiceItemError, loading: deleteInvoiceItemLoading },
  ] = useDeleteInvoiceItemMutation();

  const [
    createInvoiceItemOrder,
    {
      error: createInvoiceItemOrderError,
      loading: createInvoiceItemOrderLoading,
    },
  ] = useCreateInvoiceItemOrderMutation({
    update: (cache, { data }) => {
      const newInvoiceItemOrder =
        data?.createInvoiceItemOrder?.invoiceItemOrder;
      if (!newInvoiceItemOrder) return;

      const newInvoiceItemRef = cache.writeFragment({
        data: newInvoiceItemOrder,
        fragment: gql`
          fragment NewInvoiceItemOrder on InvoiceItemOrder {
            id
            orderId
            invoiceItemId
          }
        `,
      });

      cache.modify({
        id: cache.identify({
          __typename: 'InvoiceItem',
          id: newInvoiceItemOrder.invoiceItemId,
        }),
        fields: {
          invoiceItemOrdersByInvoiceItemId: (existingRefs) => ({
            nodes: [...existingRefs.nodes, newInvoiceItemRef],
          }),
        },
      });
    },
  });
  const [
    deleteInvoiceItemOrder,
    {
      error: deleteInvoiceItemOrderError,
      loading: deleteInvoiceItemOrderLoading,
    },
  ] = useDeleteInvoiceItemOrderByIdMutation();

  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  const saveError =
    createInvoiceError ||
    updateInvoiceError ||
    createInvoiceItemError ||
    updateInvoiceItemError ||
    deleteInvoiceItemError ||
    createInvoiceItemOrderError ||
    deleteInvoiceItemOrderError;

  const isSaving =
    createInvoiceLoading ||
    updateInvoiceLoading ||
    deleteInvoiceLoading ||
    createInvoiceItemLoading ||
    updateInvoiceItemLoading ||
    deleteInvoiceItemLoading ||
    createInvoiceItemOrderLoading ||
    deleteInvoiceItemOrderLoading;

  const defaultValues = useMemo(
    () => getInvoiceDefaultValues(orderOptions, invoice),
    [orderOptions, invoice],
  );

  const {
    handleSubmit,
    register,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<InvoiceFormFields>({
    defaultValues,
    resolver: zodResolver(InvoiceSchema),
  });

  useEffect(() => reset(defaultValues), [defaultValues, reset]);

  const onFormSubmit = async (data: InvoiceFormFields) => {
    setSaveSuccess(false);
    const {
      invoiceNumber,
      currency,
      dueDate,
      invoiceItems = [],
      payingOrganizationId,
    } = data;
    let invoiceId: number | undefined;
    if (invoice) {
      invoiceId = invoice.id;
      await updateInvoice({
        variables: {
          input: {
            id: invoiceId,
            invoicePatch: {
              invoiceNumber,
              currency,
              dueDate: new Date(dueDate).toISOString(),
              payingOrganizationId,
            },
          },
        },
      });
    } else {
      const result = await createInvoice({
        variables: {
          input: {
            invoice: {
              invoiceNumber,
              currency,
              dueDate: new Date(dueDate).toISOString(),
              voyageCharterId,
              payingOrganizationId,
            },
          },
        },
      });
      invoiceId = result.data?.createInvoice?.invoice?.id;
    }
    if (invoiceId == null) {
      throw new Error('Failed to save');
    }

    for (const current of invoiceItems) {
      if (!current) continue;
      let id = current.id;
      const { id: _id, relatedOrders, ...values } = current;
      const previous = defaultValues.invoiceItems?.find((i) => i.id === id);
      if (id == null) {
        const result = await createInvoiceItem({
          variables: {
            input: {
              invoiceItem: {
                invoiceId,
                ...values,
              },
            },
          },
        });
        id = result.data?.createInvoiceItem?.invoiceItem?.id ?? null;
      } else {
        await updateInvoiceItem({
          variables: {
            input: {
              id,
              invoiceItemPatch: values,
            },
          },
        });
      }
      for (const o of orderOptions) {
        const orderId = o.id;
        const previousState = previous?.relatedOrders.find(
          (o) => o.id === orderId,
        );

        const currentState = relatedOrders.find((o) => o.id === orderId);
        if (currentState && !previousState) {
          await createInvoiceItemOrder({
            variables: {
              input: {
                invoiceItemOrder: { invoiceItemId: id, orderId },
              },
            },
          });
        } else if (previousState && !currentState) {
          const invoiceItemOrderId = invoice?.invoiceItemsByInvoiceId?.nodes
            .find((i) => i?.id === id)
            ?.invoiceItemOrdersByInvoiceItemId?.nodes.find(
              (o) => o?.orderId === orderId,
            )?.id;

          if (typeof invoiceItemOrderId !== 'number') continue;

          await deleteInvoiceItemOrder({
            variables: {
              input: {
                id: invoiceItemOrderId,
              },
            },
          });
        }
      }
    }

    const deletedInvoiceItems =
      defaultValues.invoiceItems?.filter(
        (oldItem) => !invoiceItems.find((newItem) => oldItem.id === newItem.id),
      ) ?? [];

    for (const deletedItem of deletedInvoiceItems) {
      assertDefined(deletedItem.id, `expected deleted invoice item to have id`);

      await deleteInvoiceItem({
        variables: {
          input: {
            id: deletedItem.id,
          },
        },
      });
    }

    setSaveSuccess(true);
    onSave();
  };

  const handleDelete = () => {
    if (!invoice) {
      onClose?.();
      return;
    }

    deleteInvoice({
      variables: {
        input: {
          id: invoice.id,
        },
      },
    });
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'invoiceItems',
    keyName: 'key',
  });
  const itemErrors = errors.invoiceItems;
  const itemDefaults = defaultValues.invoiceItems;
  const total =
    watch('invoiceItems')?.reduce(
      (sum, item) => +(item.amount || 0) + sum,
      0,
    ) ?? 0;
  const currency = watch('currency');

  return (
    <Box pt={4} maxWidth="64rem">
      <Form onSubmit={handleSubmit(onFormSubmit)} flex={1}>
        <FormSection muted>
          <Heading fontSize={4} fontWeight="bold" mb={4}>
            {invoice ? `Invoice #${invoice?.id}` : 'New invoice'}
          </Heading>
          <FormRow>
            <input
              type="hidden"
              {...register('id', { setValueAs: stringToNumber })}
            />
            <FormField
              label="Invoice number"
              width={200}
              hasError={!!errors.invoiceNumber}
            >
              <Input {...register('invoiceNumber')} />
            </FormField>
            <FormField label="Due date" width={200} hasError={!!errors.dueDate}>
              <Controller
                control={control}
                name="dueDate"
                render={({ field: { value, onChange } }) => (
                  <DatePicker
                    onChange={onChange}
                    selected={value ? value : null}
                    hideTimePickerTypes
                    showTime={false}
                    timeZone="UTC"
                  />
                )}
              />
            </FormField>
            <FormField
              label="Currency"
              width={200}
              hasError={!!errors.currency}
            >
              <Select {...register('currency')} options={currencyOptions} />
            </FormField>
            <FormField
              label="Paying organization"
              width={250}
              hasError={!!errors.payingOrganizationId}
            >
              <Select
                allowNull
                defaultValue={defaultValues?.payingOrganizationId ?? undefined}
                {...register('payingOrganizationId', {
                  setValueAs: stringToNumber,
                })}
                options={payingOrganizationOptions}
                placeholder="Select organization"
              />
            </FormField>
          </FormRow>
        </FormSection>
        {fields.map((item, index) => {
          const errors = itemErrors && itemErrors[index];
          const defaults = itemDefaults && itemDefaults[index];
          return (
            item && (
              <FormSection>
                <FormRow key={item.key}>
                  <input
                    type="hidden"
                    {...register(`invoiceItems.${index}.id`, {
                      setValueAs: stringToNumber,
                    })}
                    defaultValue={item.id ?? undefined}
                  />
                  <FormField
                    label={`Amount`}
                    width={200}
                    hasError={!!errors?.amount}
                  >
                    <Input
                      defaultValue={defaults?.amount ?? undefined}
                      {...register(`invoiceItems.${index}.amount`, {
                        setValueAs: stringToNumber,
                      })}
                      type="number"
                    />
                  </FormField>
                  <FormField
                    label={`Cost type`}
                    width={200}
                    hasError={!!errors?.costType}
                  >
                    <Select
                      defaultValue={defaults?.costType ?? undefined}
                      {...register(`invoiceItems.${index}.costType`)}
                      options={costTypeOptions}
                    />
                  </FormField>
                  <FormField
                    label={`Note`}
                    width={200}
                    hasError={!!errors?.note}
                  >
                    <Input
                      defaultValue={defaults?.note ?? undefined}
                      {...register(`invoiceItems.${index}.note`)}
                      type="text"
                    />
                  </FormField>
                  <FormField
                    label={`Port call`}
                    width={200}
                    hasError={!!errors?.portCallId}
                  >
                    <Select
                      defaultValue={defaults?.portCallId ?? undefined}
                      {...register(`invoiceItems.${index}.portCallId`, {
                        setValueAs: stringToNumber,
                      })}
                      options={portCallOptions}
                      allowNull
                    />
                  </FormField>

                  <FormField
                    label={`Related orders`}
                    hasError={!!errors?.relatedOrders}
                  >
                    <Controller
                      defaultValue={defaults?.relatedOrders ?? []}
                      control={control}
                      name={`invoiceItems.${index}.relatedOrders`}
                      render={({ field: { value, onChange } }) => (
                        <ReactSelect
                          options={orderOptions}
                          getOptionValue={(opt) => opt.id?.toString() ?? ''}
                          placeholder="Orders"
                          aria-label="Related orders"
                          isMulti
                          value={value}
                          onChange={(newValue) => onChange(newValue ?? [])}
                          styles={{
                            container: (styles) => ({
                              ...styles,
                              width: '100%',
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 100,
                            }),
                          }}
                        />
                      )}
                    />
                  </FormField>
                </FormRow>
                <FormRow>
                  <Button variant="secondary" onPress={() => remove(index)}>
                    Remove item
                  </Button>
                </FormRow>
              </FormSection>
            )
          );
        })}
        <FormSection>
          <FormRow>
            <Button
              variant="secondary"
              my={4}
              onPress={() =>
                append({
                  id: null,
                  relatedOrders: [],
                })
              }
            >
              + Add item
            </Button>
          </FormRow>
        </FormSection>
        <FormSection>
          <FormRow>
            <Text fontWeight="bold">
              Total: {formatNumber(total, 2)} {currency}
            </Text>
          </FormRow>
          <FormRow>
            <Box
              display="flex"
              justifyContent="space-between"
              mt={4}
              width="100%"
            >
              <Box display="flex" flexDirection="row" alignItems="center">
                <Button
                  variant="primary"
                  disabled={isSaving}
                  onPress={handleSubmit(onFormSubmit)}
                  mr={3}
                >
                  Save invoice
                </Button>
                {!saveError && saveSuccess && (
                  <Text fontWeight="bold" color="#5fad09">
                    ✓ Saved successfully
                  </Text>
                )}
                {saveError && (
                  <Text fontWeight="bold" color="#FC4A15">
                    ✗ Error saving
                  </Text>
                )}
              </Box>
              <Box display="flex" flexDirection="row" alignItems="center">
                {deleteInvoiceError && (
                  <Text fontWeight="bold" color="#FC4A15" mr={3}>
                    ✗ Error deleting invoice
                  </Text>
                )}
                <Button
                  onPress={handleDelete}
                  variant="negative"
                  disabled={isSaving}
                >
                  Delete invoice
                </Button>
              </Box>
            </Box>
          </FormRow>
        </FormSection>
      </Form>
    </Box>
  );
};

const Invoices = ({ voyageCharterId }: InvoicesProps): JSX.Element | null => {
  const { data, loading, refetch } = useInvoicesByIdQuery({
    variables: { voyageCharterId },
  });
  const [newInvoice, setNewInvoice] = useState(false);
  const vc = data?.voyageCharterById;
  const formOptions = useMemo(() => getInvoiceFormOptions(data), [data]);

  return loading ? (
    <Box p={4}>
      <Text fontWeight="bold" color="#5fad09">
        Loading...
      </Text>
    </Box>
  ) : vc ? (
    <Box p={4}>
      <Button
        mr={3}
        variant="secondary"
        disabled={newInvoice}
        onPress={() => setNewInvoice(true)}
      >
        Add invoice
      </Button>
      {newInvoice ? (
        <InvoiceForm
          onSave={async () => {
            await delay(3000);
            await refetch();
            setNewInvoice(false);
          }}
          onClose={() => setNewInvoice(false)}
          formOptions={formOptions}
          voyageCharterId={voyageCharterId}
        />
      ) : (
        <></>
      )}
      {vc?.invoicesByVoyageCharterId?.nodes
        .map(
          (i) =>
            i && (
              <InvoiceForm
                key={i?.id}
                invoice={i}
                onSave={refetch}
                formOptions={formOptions}
                voyageCharterId={voyageCharterId}
              />
            ),
        )
        .reverse()}
    </Box>
  ) : (
    <Box p={4}>
      <Text fontWeight="bold" color="#FC4A15">
        ✗ Your account needs more privileges to view this voyage charter and
        related invoices
      </Text>
    </Box>
  );
};

export default Invoices;
